import { Button } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import biApi from "../../../../../../api/biApi";
import type { ClientInfoExtended, ReportTemplate } from "../../../../../../api/types/svTypes.ts";
import { getEditReportTemplateUrl } from "../../../report-lists/reports/utilities/editReportUrl.ts";
import SaveTemplateDialog from "../../../report-lists/templates/SaveTemplateDialog.tsx";
import useCurrentReportConfiguration from "../../hooks/useCurrentReportConfiguration";
import type { AppDispatch } from "../../store/store";
import { setEditReport } from "../../store/thunks/currentReportThunks";

interface Props {
  template: ReportTemplate;
  companies: ClientInfoExtended[];
  clientInfo: ClientInfoExtended;
}

export default function SaveTemplate({ template, companies, clientInfo }: Props) {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [savingError, setSavingError] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [openNewTemplateSavingDialog, setOpenNewTemplateSavingDialog] = useState(false);
  const { getCurrentReportConfigurationAsync } = useCurrentReportConfiguration();

  const directSave = useCallback(async () => {
    const reportConfiguration = await getCurrentReportConfigurationAsync();
    if (reportConfiguration === undefined) {
      return;
    }
    setIsSaving(true);
    const promise = biApi.updateReportTemplate({
      template: { ...template, configuration: reportConfiguration },
    });
    promise
      .then((result) => {
        if (result.success && result.data !== undefined) {
          sendNotification("The template was successfully saved.");
          dispatch(setEditReport(result.data));
        } else {
          setSavingError("An error occurred while saving the template.");
        }
      })
      .catch(() => {
        setSavingError("An error occurred while saving the template.");
      })
      .finally(() => setIsSaving(false));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [template, getCurrentReportConfigurationAsync]);

  useEffect(() => {
    if (savingError) {
      sendNotificationError("An error occurred while saving the template.");
    }
  }, [savingError, sendNotificationError]);

  return (
    <>
      <Button
        variant="contained"
        onClick={() => {
          if (template.isDraft) {
            setOpenNewTemplateSavingDialog(true);
            return;
          }
          directSave();
        }}
        loading={isSaving}
        disabled={isSaving}
      >
        Save Template
      </Button>
      {openNewTemplateSavingDialog && (
        <SaveTemplateDialog
          title="Save Template"
          template={template}
          getTemplateConfigurationAsync={getCurrentReportConfigurationAsync}
          onClose={() => setOpenNewTemplateSavingDialog(false)}
          onSaved={(rt) => {
            dispatch(setEditReport(rt));
            setOpenNewTemplateSavingDialog(false);
            sendNotification("The template was successfully saved.");
            navigate(getEditReportTemplateUrl(rt.organization, rt.dataSource || clientInfo.clientCode, rt.reportId));
          }}
          companies={companies}
        />
      )}
    </>
  );
}
